import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';
import { isPrimaryOffer, canApplyOfferTemplate } from '../utils/lineupOrdering';
import templateErrorMessage from '../utils/templateErrorMessage';
import TemplateConfirmDialog from './TemplateConfirmDialog';
import useIsMobile from '../../shared/hooks/useIsMobile';
import '../styles/ftux.css';

// O-5024: what this modal offers depends on WHICH act it was opened from.
//
// On the PRIMARY — which is every brand-new offer, and where it auto-opens —
// setting up a show is an event template's job: it carries the deal and the
// budget. Offer templates are refused on the primary server-side, so listing
// them here would only produce an error.
//
// On a SUPPORT, reached from that act's own template card, an offer template is
// exactly right: one act's terms, no budget.
//
// Blueprints are on both — Opendate-authored deal shapes, deliberately
// terms-only, and what a team with no templates yet has to start from.
const PRIMARY_NAV_ITEMS = [
  { key: 'scratch',    label: 'Start from scratch', subtitle: 'Blank offer' },
  { key: 'events',     label: 'Event templates',    subtitle: 'Deal + budget' },
  { key: 'blueprints', label: 'Blueprints',         subtitle: 'Curated by Opendate' },
];
const SUPPORT_NAV_ITEMS = [
  { key: 'scratch',    label: 'Start from scratch', subtitle: 'Blank offer' },
  { key: 'saved',      label: 'Saved templates',    subtitle: "Your team's library" },
  { key: 'blueprints', label: 'Blueprints',         subtitle: 'Curated by Opendate' },
];

const DEFAULT_ICON = 'fa-file-alt';

const cls = (...names) => names.filter(Boolean).join(' ');

const renderIcon = (iconName, fallback = DEFAULT_ICON) => (
  <i className={`far ${iconName ? `fa-${iconName.replace(/^fa-/, '')}` : fallback}`} />
);

const fmtRelative = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const day = 24 * 60 * 60 * 1000;
  if (diff < day) return 'Today';
  if (diff < 2 * day) return 'Yesterday';
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} weeks ago`;
  if (diff < 365 * day) return `${Math.floor(diff / (30 * day))} months ago`;
  return `${Math.floor(diff / (365 * day))} years ago`;
};

// O-2783: templates can now be multi-artist (Pool or Independent). Count the
// member slots so cards label "N artists" instead of the old hardcoded "1".
const memberCount = (t) => (t.v3_offer_template_pool_members?.length || 1);
const artistLabel = (t) => `${memberCount(t)} ${memberCount(t) === 1 ? 'artist' : 'artists'}`;
const modeLabel = (t) => (t.pool_mode === 'pool' ? 'Pool' : 'Independent');

const BlueprintCard = ({ blueprint, focused, onClick }) => (
  <button
    type="button"
    className={cls('ftux-card', focused && 'ftux-card--focused')}
    onClick={() => onClick(blueprint)}
    data-testid="blueprint-card"
  >
    <div className="ftux-card__badge-row">
      {blueprint.is_popular && <span className="ftux-badge ftux-badge--popular">Popular</span>}
      <span className="ftux-badge ftux-badge--blueprint">Blueprint</span>
      {blueprint.is_new && <span className="ftux-badge ftux-badge--new">New</span>}
    </div>
    <span className="ftux-card__icon">{renderIcon(blueprint.icon_name)}</span>
    <div className="ftux-card__title">{blueprint.name}</div>
    {blueprint.description && <div className="ftux-card__desc">{blueprint.description}</div>}
    <div className="ftux-card__meta">
      {blueprint.summary?.deal_term_count != null && (
        <>{artistLabel(blueprint)}&nbsp;·&nbsp;{blueprint.summary.operator_label}&nbsp;·&nbsp;{blueprint.summary.deal_term_count} terms</>
      )}
    </div>
  </button>
);

BlueprintCard.propTypes = {
  blueprint: PropTypes.object.isRequired,
  focused: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

// showUsage=false for EVENT templates: the index that feeds them carries no
// last_used_at, so every card read "Not yet used" no matter how often the
// booker had applied it.
const SavedTemplateCard = ({ template, focused, onClick, showUsage = true }) => (
  <button
    type="button"
    className={cls('ftux-card', focused && 'ftux-card--focused')}
    onClick={() => onClick(template)}
    data-testid="saved-template-card"
  >
    <span className="ftux-card__icon">{renderIcon(template.icon_name)}</span>
    <div className="ftux-card__title">{template.name}</div>
    {template.copied_from_v3_offer_template_name && (
      <div className="ftux-card__desc">Based on <strong>{template.copied_from_v3_offer_template_name}</strong></div>
    )}
    {!template.copied_from_v3_offer_template_name && template.description && (
      <div className="ftux-card__desc">{template.description}</div>
    )}
    <div className="ftux-card__meta">
      {memberCount(template) > 1 && (
        <>{artistLabel(template)}&nbsp;·&nbsp;{modeLabel(template)}{showUsage && <>&nbsp;·&nbsp;</>}</>
      )}
      {showUsage && (
        fmtRelative(template.last_used_at) ? `Used ${fmtRelative(template.last_used_at).toLowerCase()}` : 'Not yet used'
      )}
    </div>
  </button>
);

SavedTemplateCard.propTypes = {
  template: PropTypes.object.isRequired,
  focused: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  showUsage: PropTypes.bool,
};

const RecentItemRow = ({ template, focused, onClick, showUsage = true }) => {
  const isBlueprint = template.is_blueprint;
  const subtitle = isBlueprint
    ? template.description
    : (template.copied_from_v3_offer_template_name ? `Based on ${template.copied_from_v3_offer_template_name}` : template.description);

  return (
    <button
      type="button"
      className={cls('ftux-recent-row', focused && 'ftux-recent-row--focused')}
      onClick={() => onClick(template)}
      data-testid="recent-row"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
        <span className="ftux-card__icon">{renderIcon(template.icon_name)}</span>
        <div style={{ minWidth: 0 }}>
          <div className="ftux-recent-title-row">
            <span className="ftux-card__title" style={{ marginBottom: 0 }}>{template.name}</span>
            <span className={cls('ftux-badge', isBlueprint ? 'ftux-badge--blueprint' : 'ftux-badge--template')}>
              {isBlueprint ? 'Blueprint' : 'Template'}
            </span>
          </div>
          {subtitle && (
            <div className="ftux-card__meta" style={{ marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      <div className="ftux-recent-time-wrap">
        <span>{showUsage ? fmtRelative(template.last_used_at) : null}</span>
        <i className="far fa-chevron-right" />
      </div>
    </button>
  );
};

RecentItemRow.propTypes = {
  template: PropTypes.object.isRequired,
  focused: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  showUsage: PropTypes.bool,
};

const ScratchPane = ({ onStartBlank, busy }) => (
  <div className="ftux-scratch-hero">
    <div className="ftux-scratch-icon-wrap"><i className="far fa-file-alt" /></div>
    <h3 className="ftux-scratch-title">Build it manually</h3>
    <p className="ftux-scratch-subtitle">
      Start with a blank offer. Add artists, set ticket scaling, and configure income and expenses as you go.
      You can save your work as a template at any time.
    </p>
    <div className="ftux-pill-row">
      <div className="ftux-pill">
        <div className="ftux-pill__label">Setup time</div>
        <div className="ftux-pill__value">~ 4 min</div>
      </div>
      <div className="ftux-pill">
        <div className="ftux-pill__label">Best for</div>
        <div className="ftux-pill__value">One-off deals</div>
      </div>
      <div className="ftux-pill">
        <div className="ftux-pill__label">Saves as template</div>
        <div className="ftux-pill__value">Yes, when done</div>
      </div>
    </div>
    <button type="button" className="ftux-cta-button" onClick={onStartBlank} disabled={busy} data-testid="start-blank-cta">
      Start blank offer&nbsp;&nbsp;<i className="far fa-arrow-right" />
    </button>
  </div>
);

ScratchPane.propTypes = {
  onStartBlank: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

const V3NewOfferFTUXModal = ({ open, onClose, team, offer, csrfToken, initialNav = null }) => {
  const isMobile = useIsMobile();
  const [rawActiveNav, setActiveNav] = useState('scratch');
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [focusIndex, setFocusIndex] = useState(0);
  const [blueprints, setBlueprints] = useState([]);
  const [eventTemplates, setEventTemplates] = useState([]);
  const [templates, setTemplates] = useState([]);
  const primary = isPrimaryOffer(offer);
  // Blueprints are OFFER templates, so the server refuses them on the headliner
  // of a real lineup — every click there is a guaranteed 422, and blueprints is
  // the DEFAULT pane for a team with no event templates, so it was the first
  // thing such a booker hit. EventTemplateDropdown has always asked this
  // question ten lines from here; this component only ever asked the other one.
  const canApplyOffer = canApplyOfferTemplate(offer);
  const NAV_ITEMS = (primary ? PRIMARY_NAV_ITEMS : SUPPORT_NAV_ITEMS)
    .filter((item) => item.key !== 'blueprints' || canApplyOffer);
  // This modal is mounted unconditionally and reused, so its state outlives a
  // close. `activeNav` could therefore still say 'events' — with the primary's
  // event-template list still in state — when it reopens from a SUPPORT's card.
  // Rendering that pane offered a support one click that POSTs
  // apply_event_template against v3_budget_id, which is the WHOLE show's budget:
  // every line item, bracket, bonus and deposit destroyed, the primary's deal
  // overwritten, placeholder siblings minted, and no confirm on this path.
  //
  // Resolving the pane against NAV_ITEMS kills the class rather than the one
  // symptom: a pane this act has no nav item for cannot render or be applied,
  // however activeNav got its value.
  const activeNav = NAV_ITEMS.some(n => n.key === rawActiveNav) ? rawActiveNav : 'scratch';
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const userInteractedRef = useRef(false);
  const searchRef = useRef(null);
  const [pendingEventTemplate, setPendingEventTemplate] = useState(null);

  const authHeaders = { 'X-CSRF-TOKEN': csrfToken };

  useEffect(() => {
    if (!open) {
      userInteractedRef.current = false;
      setTemplatesLoaded(false);
      // Belt to the resolver's braces: drop the pane and the lists so a reopen
      // cannot show the previous act's templates even for one frame.
      setActiveNav('scratch');
      setEventTemplates([]);
      setTemplates([]);
      setPendingEventTemplate(null);
      return;
    }
    axios.get(`/teams/${team.id}/offer_blueprints/v3`)
      .then(r => setBlueprints(r.data))
      .catch(() => toast.error("Couldn't load blueprints. Please refresh and try again."));
    if (primary) {
      axios.get(`/teams/${team.id}/event_templates.json`, { params: { v3_backed: true } })
        .then(r => setEventTemplates(r.data))
        .catch(() => toast.error("Couldn't load event templates. Please refresh and try again."))
        // Always, not just on success: the pane defaulting waits on this, so a
        // failed fetch used to leave the previous pane up indefinitely.
        .finally(() => setTemplatesLoaded(true));
    } else {
      axios.get(`/teams/${team.id}/offer_templates/v3`)
        .then(r => setTemplates(r.data))
        .catch(() => toast.error("Couldn't load saved templates. Please refresh and try again."))
        .finally(() => setTemplatesLoaded(true));
    }
  }, [open, team.id, primary]);

  useEffect(() => {
    if (!open || userInteractedRef.current) return;
    // Opened deliberately at a pane — the bar's "Browse blueprints", which is a
    // solo show's only route back here. Honoured before templatesLoaded, since
    // blueprints load on their own request and must not wait on the other one.
    if (initialNav) { setActiveNav(initialNav); return; }
    if (!templatesLoaded) return;
    const list = primary ? eventTemplates : templates;
    // Falling back to blueprints when they are not on offer would land the
    // booker on an empty pane — or, before the filter above, on a guaranteed 422.
    const empty = NAV_ITEMS.some((n) => n.key === 'blueprints') ? 'blueprints' : 'scratch';
    setActiveNav(list.length > 0 ? (primary ? 'events' : 'saved') : empty);
  }, [open, templatesLoaded, primary, eventTemplates.length, templates.length, initialNav]);

  const handleNavClick = useCallback((key) => {
    userInteractedRef.current = true;
    setActiveNav(key);
  }, []);

  useEffect(() => { setFocusIndex(0); }, [activeNav, query, categoryFilter]);

  const lowerQuery = query.trim().toLowerCase();
  const matches = useCallback((item) => {
    if (!lowerQuery) return true;
    return (item.name || '').toLowerCase().includes(lowerQuery) ||
           (item.description || '').toLowerCase().includes(lowerQuery);
  }, [lowerQuery]);

  const filteredBlueprints = useMemo(() => (
    blueprints
      .filter(matches)
      .filter(bp => !categoryFilter || bp.category === categoryFilter)
  ), [blueprints, matches, categoryFilter]);

  const filteredEventTemplates = useMemo(() => eventTemplates.filter(matches), [eventTemplates, matches]);
  const filteredTemplates = useMemo(() => templates.filter(matches), [templates, matches]);

  const activePaneItems = useMemo(() => {
    if (activeNav === 'blueprints') return filteredBlueprints;
    if (activeNav === 'events') return filteredEventTemplates;
    if (activeNav === 'saved') return filteredTemplates;
    return [];
  }, [activeNav, filteredBlueprints, filteredEventTemplates, filteredTemplates]);

  const dismissAndClose = useCallback(async () => {
    try {
      await axios.patch(`/teams/${team.id}/offers/v3/${offer.id}`, { v3_offer: { ftux_dismissed: true } }, { headers: authHeaders });
    } catch (_) { /* non-fatal */ }
    onClose();
  }, [team.id, offer.id, onClose]);

  const handleStartBlank = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(`/teams/${team.id}/offers/v3/${offer.id}`, { v3_offer: { ftux_dismissed: true } }, { headers: authHeaders });
      onClose();
    } catch (e) {
      toast.error("Couldn't update offer. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [busy, team.id, offer.id, onClose]);

  const handleApplyTemplate = useCallback(async (template) => {
    if (busy) return;
    setBusy(true);
    try {
      // An event template carries both halves and is applied to the BUDGET, which
      // is what the whole show shares. A blueprint is an offer template and
      // applies terms to this offer.
      //
      // `primary &&` is deliberate belt-and-braces with the nav resolver above:
      // this request rebuilds the entire shared budget, so the one call that can
      // do that is gated on the act itself rather than on a piece of UI state.
      if (primary && activeNav === 'events') {
        await axios.post(
          `/teams/${team.id}/budgets/v3/${offer.v3_budget_id}/apply_event_template`,
          { event_template_id: template.id },
          { headers: authHeaders },
        );
        window.location.reload();
        return;
      }
      // apply_template sets ftux_dismissed inside the same transaction.
      // Reload after success: apply_template destroys + recreates deal terms and
      // overwrites all COPY_FROM_TEMPLATE fields on the server. OfferTabsSection
      // caches localOffer/localBudget derived from initial mount, so dispatching
      // would leave that local cache stale until next interaction. Reload is the
      // cheapest way to guarantee the page reflects the new server state.
      await axios.post(`/teams/${team.id}/offer_templates/v3/${template.id}/apply_template`, { v3_offer_id: offer.id }, { headers: authHeaders });
      window.location.reload();
    } catch (e) {
      // A 422 here is a permanent refusal with a reason — recorded deposits, a
      // paid settlement, the primary-offer rule. "Please try again" sent the
      // booker round the same loop forever. The bar's copy of this apply has
      // always been diagnosable; this one was not.
      toast.error(templateErrorMessage(e, "Couldn't apply this template. Please try again."));
      setBusy(false);
    }
  }, [busy, team.id, offer.id, offer.v3_budget_id, activeNav, primary]);

  // An EVENT template rebuilds the whole show's shared budget, so it asks first —
  // the same request from EventTemplateDropdown has always been gated behind
  // TemplateConfirmDialog, and this copy went straight through on a single
  // click. A blueprint or saved template applies one act's terms, which is what
  // this modal is for and what the per-offer card does unconfirmed.
  const requestApplyTemplate = useCallback((template) => {
    if (primary && activeNav === 'events') {
      setPendingEventTemplate(template);
      return undefined;
    }
    return handleApplyTemplate(template);
  }, [primary, activeNav, handleApplyTemplate]);

  const handleConfirm = useCallback(() => {
    if (activeNav === 'scratch') return handleStartBlank();
    const item = activePaneItems[focusIndex];
    if (item) return requestApplyTemplate(item);
  }, [activeNav, activePaneItems, focusIndex, handleStartBlank, requestApplyTemplate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') { dismissAndClose(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusIndex(idx => Math.min(idx + 1, Math.max(0, activePaneItems.length - 1)));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusIndex(idx => Math.max(0, idx - 1));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // The handler is on the modal container, so a bare Enter while typing in
      // the search box used to apply whatever happened to be the top result.
      // Arrow into the list first — that is what selects something.
      if (document.activeElement === searchRef.current) return;
      handleConfirm();
      return;
    }
    if (e.key === '/' && document.activeElement !== searchRef.current) {
      e.preventDefault();
      searchRef.current?.focus();
    }
  }, [activePaneItems.length, handleConfirm, dismissAndClose]);

  const blueprintCategories = useMemo(() => {
    const set = new Set();
    blueprints.forEach(bp => { if (bp.category) set.add(bp.category); });
    return Array.from(set).sort();
  }, [blueprints]);

  const navCounts = {
    events: eventTemplates.length,
    saved: templates.length,
    blueprints: blueprints.length,
  };

  return (
    <Popup
      open={open}
      onClose={dismissAndClose}
      modal
      closeOnDocumentClick={false}
      closeOnEscape={false}
      contentStyle={{ width: '900px', maxWidth: 'calc(100vw - 16px)', padding: 0, overflow: 'hidden', border: 'none', borderRadius: '12px', background: 'var(--sl-bg-primary)', color: 'var(--sl-text-primary)' }}
      className="ftux-popup"
    >
      <div className="ftux-popup-content">
        <div className="ftux-layout" onKeyDown={handleKeyDown} tabIndex={-1} data-testid="ftux-modal">
          <aside className="ftux-rail">
            <span className="ftux-rail-eyebrow"><i className="far fa-file-alt" /> New offer</span>
            <h2 className="ftux-rail-heading">How do you want to start?</h2>
            <div className="ftux-search-wrap">
              <i className="far fa-search ftux-search-icon" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search event templates &amp; blueprints"
                className="ftux-search-input"
                data-testid="ftux-search"
              />
            </div>
            {NAV_ITEMS.map(item => {
              const isActive = activeNav === item.key;
              const count = navCounts[item.key];
              return (
                <div
                  key={item.key}
                  className={cls('ftux-nav-item', isActive && 'ftux-nav-item--active')}
                  onClick={() => handleNavClick(item.key)}
                  data-testid={`ftux-nav-${item.key}`}
                >
                  <div>
                    <div className="ftux-nav-item__label">{item.label}</div>
                    <div className="ftux-nav-subtitle">{item.subtitle}</div>
                  </div>
                  {count != null && count > 0 && <span className="ftux-nav-badge">{count}</span>}
                </div>
              );
            })}
          </aside>

          <section className="ftux-pane">
            <header className="ftux-pane-header">
              <div>
                <h3 className="ftux-pane-header__title">
                  {activeNav === 'scratch' && 'Start from scratch'}
                  {activeNav === 'events' && 'Event templates'}
                  {activeNav === 'saved' && 'Your saved templates'}
                  {activeNav === 'blueprints' && 'Blueprints'}
                </h3>
                <p className="ftux-pane-header__subtitle">
                  {activeNav === 'scratch' && 'Build the offer manually from a blank slate.'}
                  {activeNav === 'events' && 'A saved deal and budget together — how a show like this usually runs.'}
                  {activeNav === 'saved' && "A saved deal for one act. Applies this artist's terms only."}
                  {activeNav === 'blueprints' && 'Pre-built starting points curated by Opendate.'}
                </p>
              </div>
              <div className="ftux-pane-header__actions">
                {activeNav === 'blueprints' && blueprintCategories.length > 0 && (
                  <select
                    className="ftux-dropdown"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    data-testid="ftux-category-filter"
                  >
                    <option value="">Deal type. All</option>
                    {blueprintCategories.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
                  </select>
                )}
                <button type="button" className="ftux-close" onClick={dismissAndClose} aria-label="Close">
                  <i className="far fa-times" />
                </button>
              </div>
            </header>

            <div className="ftux-pane-body">
              {activeNav === 'scratch' && <ScratchPane onStartBlank={handleStartBlank} busy={busy} />}

              {activeNav === 'saved' && (
                filteredTemplates.length === 0
                  ? (
                    <div className="ftux-empty-state">
                      {query
                        ? 'No templates match your search.'
                        : "You don't have any saved offer templates yet. Save one from an act's deal to reuse it."}
                    </div>
                  )
                  : (
                    <div className={isMobile ? 'ftux-list' : 'ftux-grid'}>
                      {filteredTemplates.map((t, i) => (
                        isMobile
                          ? <RecentItemRow key={t.id} template={t} focused={i === focusIndex} onClick={requestApplyTemplate} />
                          : <SavedTemplateCard key={t.id} template={t} focused={i === focusIndex} onClick={requestApplyTemplate} />
                      ))}
                    </div>
                  )
              )}

              {activeNav === 'events' && (
                filteredEventTemplates.length === 0
                  ? (
                    <div className="ftux-empty-state">
                      {query
                        ? 'No event templates match your search.'
                        : "You don't have any event templates yet. Save one from the offer editor to set up a show like this again."}
                    </div>
                  )
                  : (
                    <div className={isMobile ? 'ftux-list' : 'ftux-grid'}>
                      {filteredEventTemplates.map((t, i) => (
                        isMobile
                          ? <RecentItemRow key={t.id} template={t} focused={i === focusIndex} onClick={requestApplyTemplate} showUsage={false} />
                          : <SavedTemplateCard key={t.id} template={t} focused={i === focusIndex} onClick={requestApplyTemplate} showUsage={false} />
                      ))}
                    </div>
                  )
              )}

              {activeNav === 'blueprints' && (
                filteredBlueprints.length === 0
                  ? <div className="ftux-empty-state">No blueprints match your filters.</div>
                  : (
                    <div className={isMobile ? 'ftux-list' : 'ftux-grid'}>
                      {filteredBlueprints.map((bp, i) => (
                        isMobile
                          ? <RecentItemRow key={bp.id} template={bp} focused={i === focusIndex} onClick={requestApplyTemplate} />
                          : <BlueprintCard key={bp.id} blueprint={bp} focused={i === focusIndex} onClick={requestApplyTemplate} />
                      ))}
                    </div>
                  )
              )}
            </div>

            <footer className="ftux-pane-footer">
              <div>
                <span className="ftux-kbd-hint"><span className="ftux-kbd-key">↩</span> Open</span>
                <span className="ftux-kbd-hint"><span className="ftux-kbd-key">↑</span><span className="ftux-kbd-key">↓</span> Navigate</span>
                <span className="ftux-kbd-hint"><span className="ftux-kbd-key">esc</span> Close</span>
              </div>
              <button type="button" className="ftux-dropdown" style={{ padding: '6px 14px' }} onClick={dismissAndClose}>Cancel</button>
            </footer>
          </section>
        </div>
      </div>

      {/* Same blast radius, and now the same warning, as the bar's dropdown:
        * this rebuilds every line item on the show's shared budget, plus its
        * brackets, bonuses and deposits, and can add offers for acts the show
        * does not have yet. */}
      <TemplateConfirmDialog
        open={!!pendingEventTemplate}
        title={`Apply “${pendingEventTemplate?.name}”?`}
        body={
          'This replaces every line item on this show\u2019s budget \u2014 plus its deal '
          + 'brackets, bonuses and deposits \u2014 for every artist on the show, and '
          + 'overwrites the headliner\u2019s deal terms. If the template covers more '
          + 'acts than this show has, it adds an offer for each one, with a '
          + 'placeholder artist for you to replace.'
        }
        confirmLabel="Apply template"
        busyLabel="Applying..."
        busy={busy}
        destructive
        onConfirm={() => { const t = pendingEventTemplate; setPendingEventTemplate(null); handleApplyTemplate(t); }}
        onCancel={() => setPendingEventTemplate(null)}
        testId="ftux-event-template-apply-confirm"
      />
    </Popup>
  );
};

V3NewOfferFTUXModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  team: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired }).isRequired,
  offer: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired }).isRequired,
  csrfToken: PropTypes.string.isRequired,
  initialNav: PropTypes.oneOf(['scratch', 'events', 'saved', 'blueprints']),
};

export default V3NewOfferFTUXModal;
